import { fabric } from "fabric";
import { baseApi, imgUrl } from "../api/api";

/**
 * Rewrite production/stored media URLs to the current API host (e.g. localhost:5001 in dev).
 * Keeps data/blob URLs unchanged.
 */
export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string") return url;
  const trimmed = url.trim();
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) return trimmed;

  const publicMatch = trimmed.match(/\/public\/(.+)$/i);
  if (publicMatch) {
    return `${imgUrl}/public/${publicMatch[1]}`;
  }
  if (trimmed.startsWith("/public/")) {
    return `${imgUrl}${trimmed}`;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return `${imgUrl}/public/${trimmed.replace(/^\//, "")}`;
  }
  return trimmed;
}

/** Built-in editor assets (icons) under /public on the current API host. */
export function publicAssetUrl(filename) {
  return resolveMediaUrl(`/public/${filename}`);
}

function isCrossOriginMediaUrl(url) {
  if (typeof window === "undefined") return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

function loadFabricFromSrc(src, useCrossOrigin) {
  return new Promise((resolve, reject) => {
    const opts = useCrossOrigin ? { crossOrigin: "anonymous" } : {};
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error("Image load timeout"));
      }
    }, 30000);

    fabric.Image.fromURL(
      src,
      (img) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (img && img.width > 0 && img.height > 0) {
          resolve(img);
        } else {
          reject(new Error("Invalid image"));
        }
      },
      opts
    );
  });
}

async function fetchAsBlobUrl(src) {
  const res = await fetch(src, { credentials: "include", mode: "cors" });
  if (!res.ok) {
    throw new Error(`Fetch failed: ${res.status}`);
  }
  const blob = await res.blob();
  if (!blob.size) {
    throw new Error("Empty media response");
  }
  return URL.createObjectURL(blob);
}

/**
 * Load an image for Fabric.js without CORS taint (dev: localhost:3000 → :5001 / proxy).
 * @returns {{ img: fabric.Image, storageUrl: string }}
 */
export async function loadImageForFabric(originalUrl) {
  const storageUrl = originalUrl;
  const resolved = resolveMediaUrl(originalUrl);

  if (!isCrossOriginMediaUrl(resolved)) {
    try {
      const img = await loadFabricFromSrc(resolved, true);
      return { img, storageUrl };
    } catch (_) {
      /* fall through to fetch/proxy */
    }
  }

  const sources = [
    resolved,
    `${baseApi}/content/proxy-media?url=${encodeURIComponent(originalUrl)}`,
  ];
  if (originalUrl !== resolved && /^https?:\/\//i.test(originalUrl)) {
    sources.push(originalUrl);
  }

  let lastError;
  for (const src of sources) {
    let blobUrl;
    try {
      blobUrl = await fetchAsBlobUrl(src);
      const img = await loadFabricFromSrc(blobUrl, false);
      URL.revokeObjectURL(blobUrl);
      return { img, storageUrl };
    } catch (err) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      lastError = err;
      try {
        const img = await loadFabricFromSrc(src, true);
        return { img, storageUrl };
      } catch (directErr) {
        lastError = directErr;
      }
    }
  }
  throw lastError || new Error("Could not load image");
}
